import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, BookOpen, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from '../config/tenant';
import { cardTheme } from '../styles/cardTheme';
import SEOHead from '../components/common/SEOHead';
import { BRAND_NAME, BRAND_TEACHER_NAME, SITE_URL } from '../config/brand';
import { teacherGradePath } from '../utils/teacherRoutes';

type TeacherSubject = {
  id: string;
  name: string;
  description?: string;
  code?: string;
  gradeName?: string;
  gradeId?: string;
  courseCount?: number;
};

type TeacherGrade = {
  gradeName: string;
  gradeId: string;
  subjects: TeacherSubject[];
  courseCount: number;
};

const GRADE_ORDER = [
  'الأول الإعدادي',
  'أولى إعدادي',
  'الثاني الإعدادي',
  'ثانية إعدادي',
  'الثالث الإعدادي',
  'ثالثة إعدادي',
  'الأول الثانوي',
  'أولى ثانوي',
  'الثاني الثانوي',
  'ثانية ثانوي',
  'الثالث الثانوي',
  'ثالثة ثانوي',
  'تالتة ثانوي',
];

function getGradeValue(gradeName?: string): string {
  if (!gradeName) return 'secondary1';
  if (gradeName.includes('الأول الثانوي') || gradeName.includes('أولى ثانوي')) return 'secondary1';
  if (gradeName.includes('الثاني الثانوي') || gradeName.includes('ثانية ثانوي')) return 'secondary2';
  if (gradeName.includes('الثالث الثانوي') || gradeName.includes('ثالثة ثانوي') || gradeName.includes('تالتة ثانوي')) {
    return 'secondary3';
  }
  if (gradeName.includes('الأول الإعدادي') || gradeName.includes('أولى إعدادي')) return 'prep1';
  if (gradeName.includes('الثاني الإعدادي') || gradeName.includes('ثانية إعدادي')) return 'prep2';
  if (gradeName.includes('الثالث الإعدادي') || gradeName.includes('ثالثة إعدادي')) return 'prep3';
  return 'secondary1';
}

function toGradeStateKey(gradeValue: string): string {
  const map: Record<string, string> = {
    secondary1: 'Secondary1',
    secondary2: 'Secondary2',
    secondary3: 'Secondary3',
    prep1: 'Prep1',
    prep2: 'Prep2',
    prep3: 'Prep3',
  };
  return map[gradeValue] ?? 'Secondary1';
}

function gradeSortIndex(gradeName: string): number {
  const idx = GRADE_ORDER.findIndex((label) => gradeName.includes(label) || label.includes(gradeName));
  return idx === -1 ? 999 : idx;
}

function isRecognizedGradeName(gradeName?: string): boolean {
  if (!gradeName) return false;
  return GRADE_ORDER.some((label) => gradeName.includes(label) || label.includes(gradeName));
}

function groupSubjectsByGrade(subjects: TeacherSubject[]): TeacherGrade[] {
  const map = new Map<string, TeacherGrade>();

  subjects.forEach((subject) => {
    const key = isRecognizedGradeName(subject.gradeName)
      ? getGradeValue(subject.gradeName)
      : subject.gradeId || subject.gradeName || 'unknown';
    const gradeName = subject.gradeName || 'مرحلة غير محددة';
    const existing = map.get(key);

    if (existing) {
      existing.subjects.push(subject);
      existing.courseCount += subject.courseCount ?? 0;
    } else {
      map.set(key, {
        gradeId: subject.gradeId || key,
        gradeName,
        subjects: [subject],
        courseCount: subject.courseCount ?? 0,
      });
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => gradeSortIndex(a.gradeName) - gradeSortIndex(b.gradeName)
  );
}

const TeacherPage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    imagePath?: string;
    department?: string;
    biography?: string;
    yearsOfExperience?: number;
    active?: boolean;
  } | null>(null);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingGrades, setLoadingGrades] = useState(true);

  const grades = useMemo(() => groupSubjectsByGrade(subjects), [subjects]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!teacherId) return;

      try {
        setLoading(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            firstName: string;
            lastName: string;
            fullName: string;
            imagePath?: string;
            department?: string;
            biography?: string;
            yearsOfExperience?: number;
            active: boolean;
          }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        const found = response.data?.data?.find((t) => t.id === teacherId);
        setTeacher(
          found ?? {
            id: teacherId,
            firstName: 'مدرس',
            lastName: '',
            fullName: 'مدرس',
            active: true,
          }
        );
      } catch {
        setTeacher({
          id: teacherId,
          firstName: 'مدرس',
          lastName: '',
          fullName: 'مدرس',
          active: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [teacherId]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!teacherId) return;

      try {
        setLoadingGrades(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            name: string;
            description?: string;
            code?: string;
            gradeName?: string;
            gradeId?: string;
            courseCount?: number;
          }>;
        }>(`${BASE_URL}api/Teacher/${teacherId}/subjects`, {
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data.data) {
          setSubjects(
            response.data.data.map((subject) => {
              const raw = subject as typeof subject & { Id?: string; Name?: string; GradeName?: string; GradeId?: string; CourseCount?: number };
              return {
                id: String(raw.id ?? raw.Id ?? ''),
                name: String(raw.name ?? raw.Name ?? ''),
                description: subject.description,
                code: subject.code,
                gradeName: raw.gradeName ?? raw.GradeName,
                gradeId: raw.gradeId ?? raw.GradeId,
                courseCount: raw.courseCount ?? raw.CourseCount,
              };
            })
          );
        } else {
          setSubjects([]);
        }
      } catch {
        setSubjects([]);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchSubjects();
  }, [teacherId]);

  const handleGradeClick = (grade: TeacherGrade) => {
    if (!teacherId) return;
    const gradeValue = getGradeValue(grade.gradeName);
    const subjectIds = grade.subjects.map((subject) => subject.id).filter(Boolean);
    navigate(teacherGradePath(teacherId, gradeValue), {
      state: {
        grade: toGradeStateKey(gradeValue),
        teacherId,
        subjects: grade.subjects,
        subjectIds,
        subjectId: subjectIds.length === 1 ? subjectIds[0] : undefined,
      },
    });
  };

  const displayTeacher = teacher ?? {
    id: teacherId || '',
    firstName: 'مدرس',
    lastName: '',
    fullName: 'مدرس',
  };

  if (loading && !teacher) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4" dir="rtl">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-green-200 border-t-green-700" />
          <p className="text-sm text-gray-600 sm:text-base">جاري تحميل بيانات المدرس...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper font-cairo" dir="rtl">
      <SEOHead
        title={`${displayTeacher.fullName} | مدرس في ${BRAND_NAME} | ${BRAND_TEACHER_NAME}`}
        description={
          displayTeacher.biography?.trim() ||
          `تعرّف على المدرس ${displayTeacher.fullName} وكورساته على منصة نهج الأزهر — دروس أزهرية أونلاين مع الأستاذ سعيد الفقي.`
        }
        keywords={`${displayTeacher.fullName}, مدرسين أزهر, ${BRAND_NAME}, ${BRAND_TEACHER_NAME}, كورسات أزهرية`}
        url={`/teacher/${teacherId || displayTeacher.id}`}
        image={displayTeacher.imagePath || '/logo.png'}
        breadcrumbs={[{ name: displayTeacher.fullName, path: `/teacher/${teacherId || displayTeacher.id}` }]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: displayTeacher.fullName,
          description: displayTeacher.biography || `مدرس على منصة ${BRAND_NAME}`,
          url: `${SITE_URL}/teacher/${teacherId || displayTeacher.id}`,
          image: displayTeacher.imagePath || `${SITE_URL}/logo.png`,
          jobTitle: displayTeacher.department || 'مدرس',
          worksFor: {
            '@type': 'EducationalOrganization',
            name: BRAND_NAME,
            url: SITE_URL,
          },
        }}
      />
      <header className="sticky top-0 z-20 border-b border-green-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-green-800 hover:text-green-600"
          >
            <ArrowRight className="h-5 w-5" />
            العودة
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        {/* Grades only */}
        <section>
          <div className="mb-5 text-center sm:mb-6 sm:text-right">
            <div className={`mb-3 inline-flex ${cardTheme.badge}`}>
              <BookOpen className="h-3.5 w-3.5" />
              المراحل الدراسية
            </div>
            <h1 className={`text-lg font-bold sm:text-xl lg:text-2xl ${cardTheme.title}`}>
              مراحل {displayTeacher.fullName}
            </h1>
            <p className={`mt-1 text-sm sm:text-base ${cardTheme.subtitle}`}>
              اختر المرحلة لعرض كورسات هذا المدرس
            </p>
          </div>

          {loadingGrades ? (
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`${cardTheme.shell} h-36 animate-pulse bg-green-50/50 sm:h-40`}
                />
              ))}
            </div>
          ) : grades.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {grades.map((grade, index) => (
                <button
                  key={grade.gradeId || index}
                  type="button"
                  onClick={() => handleGradeClick(grade)}
                  className={`group ${cardTheme.shell} ${cardTheme.shellHover} ${cardTheme.accentTop} flex min-h-[9.5rem] flex-col p-4 text-right sm:min-h-[10.5rem] sm:p-5`}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 ${
                        index % 2 === 0 ? cardTheme.iconBox : cardTheme.iconBoxGold
                      }`}
                    >
                      <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <ChevronLeft className="mt-1 h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-green-700" />
                  </div>

                  <h3
                    className={`mb-2 line-clamp-2 text-base font-bold leading-snug sm:text-lg ${cardTheme.title} group-hover:text-green-700`}
                  >
                    {grade.gradeName}
                  </h3>

                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800 sm:text-xs">
                      <BookOpen className="h-3 w-3" />
                      {grade.subjects.length}{' '}
                      {grade.subjects.length === 1 ? 'مادة' : 'مواد'}
                    </span>
                    {grade.courseCount > 0 && (
                      <span className="inline-flex items-center rounded-full bg-gold-50 px-2.5 py-1 text-[11px] font-semibold text-gold-800 sm:text-xs">
                        {grade.courseCount} كورس
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className={`${cardTheme.shell} px-4 py-12 text-center sm:py-16`}>
              <GraduationCap className="mx-auto mb-3 h-12 w-12 text-gray-300 sm:h-14 sm:w-14" />
              <p className="text-sm font-medium text-gray-600 sm:text-base">
                لا توجد مراحل دراسية متاحة لهذا المدرس حالياً
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TeacherPage;
